/**
 * PDKS — Personel Devam Kontrol Sistemi (Faz 1).
 *
 * Bu dosya 4 sayfayı barındırır:
 *   - PdksPanel        : /pdks/panel        (bugünkü özet)
 *   - PdksMovements    : /pdks/hareketler   (günlük hareketler + manuel giriş/düzenle)
 *   - PdksTimesheet    : /pdks/puantaj      (aylık puantaj)
 *   - PdksSettingsPage : /pdks/ayarlar      (çalışma saatleri ayarları)
 *
 * Ek olarak `PdksEmployeeTab` bileşeni EmployeeProfile'daki PDKS sekmesi için
 * export edilir.
 */
import { useCallback, useEffect, useMemo, useState, Fragment } from "react";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Users, Clock, CheckCircle2, XCircle, LogIn, LogOut, AlertTriangle,
  UserCheck, UserX, Calendar as CalendarIcon, Plus, Save, Search, Loader2,
  Edit3, Trash2, RefreshCcw, Filter, ChevronDown, ChevronUp,
  Camera, KeyRound, Fingerprint, ShieldCheck, Copy, Check, Ban,
  Smartphone,
} from "lucide-react";
import { toast } from "sonner";
import { FaceEnrollDialog, PinSetDialog } from "@/pages/PdksFaceEnroll";
import { matchesAll } from "@/lib/trFold";
import { PdksActivationDialog } from "@/components/PdksActivationDialog";
import { PdksApkCard } from "@/components/PdksApkCard";

// ============================================================================
// Shared helpers
// ============================================================================

const DAYS_TR = ["Pzt", "Sal", "Çar", "Per", "Cum", "Cmt", "Paz"]; // ISO 1..7

function todayIso() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}

function monthIso(d = new Date()) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function fmtMinutes(m) {
  const total = Math.max(0, Number(m || 0));
  const h = Math.floor(total / 60);
  const mm = total % 60;
  if (h === 0) return `${mm} dk`;
  return `${h}s ${mm}dk`;
}

function fmtDateTR(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("tr-TR", { timeZone: "Europe/Istanbul" });
  } catch {
    return iso;
  }
}

const STATUS_STYLE = {
  in_office:        { label: "İşyerinde",  cls: "bg-emerald-950 text-emerald-300 border-emerald-800" },
  checked_out:      { label: "Çıkış yaptı", cls: "bg-blue-950 text-blue-300 border-blue-800" },
  not_arrived:      { label: "Henüz gelmedi", cls: "bg-slate-900 text-slate-300 border-slate-700" },
  absent:           { label: "Devamsız",    cls: "bg-rose-950 text-rose-300 border-rose-800" },
  on_leave:         { label: "İzinli",      cls: "bg-amber-950 text-amber-300 border-amber-800" },
  holiday:          { label: "Tatil",       cls: "bg-slate-900 text-slate-400 border-slate-700" },
  missing_checkout: { label: "Çıkış kaydı yok", cls: "bg-blue-950 text-blue-300 border-blue-800" },
  future:           { label: "—",           cls: "bg-slate-950 text-slate-500 border-slate-800" },
  unknown:          { label: "—",           cls: "bg-slate-900 text-slate-400 border-slate-700" },
};

function StatusBadge({ status, isLate, isEarly }) {
  const s = STATUS_STYLE[status] || STATUS_STYLE.unknown;
  return (
    <div className="flex flex-wrap gap-1">
      <Badge className={`${s.cls} rounded-sm text-[10px] uppercase tracking-wider`}>{s.label}</Badge>
      {isLate && <Badge className="bg-blue-950 text-blue-300 border-blue-800 rounded-sm text-[10px] uppercase tracking-wider">Geç kaldı</Badge>}
      {isEarly && <Badge className="bg-blue-950 text-blue-300 border-blue-800 rounded-sm text-[10px] uppercase tracking-wider">Erken çıktı</Badge>}
    </div>
  );
}

function EmployeeAvatar({ emp, size = "sm" }) {
  const s = size === "sm" ? "w-8 h-8 text-xs" : "w-10 h-10 text-sm";
  const initials = (emp?.name || "?").split(/\s+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  if (emp?.photo_url) {
    return <img src={emp.photo_url} alt="" className={`${s} rounded-full object-cover ring-1 ring-slate-700`} />;
  }
  return (
    <div className={`${s} rounded-full bg-slate-800 text-slate-300 ring-1 ring-slate-700 flex items-center justify-center font-mono uppercase`}>
      {initials || "?"}
    </div>
  );
}

// Shared meta cache
function usePdksMeta() {
  const [meta, setMeta] = useState({ event_type_labels: {}, source_labels: {}, status_labels: {} });
  useEffect(() => {
    api.get("/pdks/meta").then((r) => setMeta(r.data)).catch(() => {});
  }, []);
  return meta;
}

function useDepartments() {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    api.get("/departments/meta")
      .then((r) => {
        const codes = r.data?.departments || [];
        const labels = r.data?.labels || {};
        setRows(codes.map((c) => ({ code: c, name: labels[c] || c })));
      })
      .catch(() => setRows([]));
  }, []);
  return rows;
}

function useEmployees() {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    api.get("/employees").then((r) => setRows(r.data || [])).catch(() => setRows([]));
  }, []);
  return rows;
}

// ============================================================================
// 1) PDKS Panel — bugünkü durum
// ============================================================================

export function PdksPanel() {
  const [date, setDate] = useState(todayIso());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [manualOpen, setManualOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/pdks/dashboard", { params: { date } });
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  }, [date]);
  useEffect(() => { load(); }, [load]);
  // Panel her 20 sn'de otomatik yenile — kiosk giriş/çıkışlarını canlı gör
  useEffect(() => {
    const iv = setInterval(load, 20 * 1000);
    return () => clearInterval(iv);
  }, [load]);

  const filtered = useMemo(() => {
    if (!data?.rows) return [];
    const q = search.trim().toLocaleLowerCase("tr");
    if (!q) return data.rows;
    return data.rows.filter((r) => matchesAll(q, [r.employee.name, r.employee.department]));
  }, [data, search]);

  const c = data?.counts || {};

  return (
    <div className="p-4 sm:p-6 space-y-6" data-testid="pdks-panel-page">
      <PageHeader
        title="PDKS Paneli"
        subtitle="Personel Devam Kontrol Sistemi · Bugünkü durum"
      />

      {/* Date + refresh + manual */}
      <div className="flex flex-wrap gap-2 items-center">
        <div className="flex items-center gap-2">
          <CalendarIcon className="w-4 h-4 text-slate-400" />
          <Input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="bg-slate-950 border-slate-800 rounded-sm w-40 h-9 text-xs font-mono"
            data-testid="pdks-panel-date"
          />
        </div>
        <Button variant="outline" size="sm" onClick={load} className="border-slate-700 rounded-sm h-9" data-testid="pdks-panel-refresh">
          <RefreshCcw className="w-3.5 h-3.5 mr-1" /> Yenile
        </Button>
        <div className="flex-1 min-w-[200px] relative">
          <Search className="w-3.5 h-3.5 text-slate-500 absolute left-2.5 top-1/2 -translate-y-1/2" />
          <Input
            placeholder="Personel / departman ara..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="bg-slate-950 border-slate-800 rounded-sm pl-8 h-9 text-xs"
            data-testid="pdks-panel-search"
          />
        </div>
        <Button onClick={() => setManualOpen(true)} className="bg-blue-600 hover:bg-blue-500 rounded-sm h-9" data-testid="pdks-panel-add-btn">
          <Plus className="w-3.5 h-3.5 mr-1" /> Manuel Giriş/Çıkış
        </Button>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-9 gap-2">
        <KpiCard icon={<Users className="w-4 h-4" />} label="Toplam" value={c.total ?? 0} tone="slate" testId="kpi-total" />
        <KpiCard icon={<UserCheck className="w-4 h-4" />} label="İşe Gelen" value={c.arrived ?? 0} tone="emerald" testId="kpi-arrived" />
        <KpiCard icon={<UserX className="w-4 h-4" />} label="Gelmeyen" value={c.not_arrived ?? 0} tone="slate" testId="kpi-not-arrived" />
        <KpiCard icon={<LogIn className="w-4 h-4" />} label="Şu An İçeride" value={c.in_office ?? 0} tone="emerald" testId="kpi-in-office" />
        <KpiCard icon={<LogOut className="w-4 h-4" />} label="Çıkış Yapan" value={c.checked_out ?? 0} tone="blue" testId="kpi-checked-out" />
        <KpiCard icon={<Clock className="w-4 h-4" />} label="Geç Kalan" value={c.late ?? 0} tone="orange" testId="kpi-late" />
        <KpiCard icon={<AlertTriangle className="w-4 h-4" />} label="Erken Çıkan" value={c.early_leave ?? 0} tone="orange" testId="kpi-early" />
        <KpiCard icon={<CheckCircle2 className="w-4 h-4" />} label="İzinli" value={c.on_leave ?? 0} tone="amber" testId="kpi-leave" />
        <KpiCard icon={<XCircle className="w-4 h-4" />} label="Devamsız" value={c.absent ?? 0} tone="rose" testId="kpi-absent" />
      </div>

      {/* Table */}
      <Card className="bg-slate-900/40 border-slate-800 rounded-sm overflow-hidden" data-testid="pdks-panel-table-card">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-slate-950/70 text-slate-400 uppercase tracking-wider text-[10px]">
                <th className="text-left px-3 py-2">Personel</th>
                <th className="text-left px-3 py-2">Departman</th>
                <th className="text-left px-3 py-2">İlk Giriş</th>
                <th className="text-left px-3 py-2">Son Çıkış</th>
                <th className="text-right px-3 py-2">Toplam</th>
                <th className="text-right px-3 py-2">Geç</th>
                <th className="text-right px-3 py-2">Erken</th>
                <th className="text-left px-3 py-2">Durum</th>
                <th className="text-left px-3 py-2">Kaynak</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {loading ? (
                <tr><td colSpan={9} className="text-center py-6 text-slate-500"><Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Yükleniyor...</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={9} className="text-center py-6 text-slate-500">Kayıt yok.</td></tr>
              ) : filtered.map((r) => (
                <tr key={r.employee.id} className="text-slate-300 hover:bg-slate-900/40" data-testid={`pdks-row-${r.employee.id}`}>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2">
                      <EmployeeAvatar emp={r.employee} />
                      <div>
                        <div className="text-slate-100">{r.employee.name}</div>
                        {r.employee.position && <div className="text-[10px] text-slate-500">{r.employee.position}</div>}
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-2 text-slate-400">{r.employee.department || "—"}</td>
                  <td className="px-3 py-2 font-mono text-[11px]">{r.first_check_in || "—"}</td>
                  <td className="px-3 py-2 font-mono text-[11px]">{r.last_check_out || "—"}</td>
                  <td className="px-3 py-2 text-right font-mono">{fmtMinutes(r.worked_minutes)}</td>
                  <td className={`px-3 py-2 text-right font-mono ${r.late_minutes > 0 ? "text-blue-400" : ""}`}>{r.late_minutes > 0 ? `${r.late_minutes} dk` : "—"}</td>
                  <td className={`px-3 py-2 text-right font-mono ${r.early_leave_minutes > 0 ? "text-blue-400" : ""}`}>{r.early_leave_minutes > 0 ? `${r.early_leave_minutes} dk` : "—"}</td>
                  <td className="px-3 py-2"><StatusBadge status={r.status} isLate={r.is_late} isEarly={r.is_early} /></td>
                  <td className="px-3 py-2 text-slate-500 text-[10px] uppercase tracking-wider">{r.source || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <ManualEntryDialog open={manualOpen} onClose={() => setManualOpen(false)} defaultDate={date} onCreated={load} />
    </div>
  );
}

function KpiCard({ icon, label, value, tone = "slate", testId }) {
  const tones = {
    slate: "text-slate-300",
    emerald: "text-emerald-400",
    blue: "text-blue-400",
    orange: "text-blue-400",
    amber: "text-amber-400",
    rose: "text-rose-400",
  };
  return (
    <Card className="bg-slate-900/40 border-slate-800 rounded-sm p-3" data-testid={testId}>
      <div className={`flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-mono ${tones[tone]}`}>
        {icon}
        <span>{label}</span>
      </div>
      <div className="text-2xl text-slate-100 font-semibold mt-1">{value}</div>
    </Card>
  );
}

// ============================================================================
// 2) Günlük Hareketler
// ============================================================================

export function PdksMovements() {
  const meta = usePdksMeta();
  const departments = useDepartments();
  const [filters, setFilters] = useState({
    date: todayIso(), start_date: "", end_date: "", employee_id: "",
    department: "", event_type: "", source: "",
  });
  const [rows, setRows] = useState([]);
  const [empMap, setEmpMap] = useState({});
  const [loading, setLoading] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [editEvent, setEditEvent] = useState(null);
  const [expanded, setExpanded] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
      const { data } = await api.get("/pdks/events", { params });
      setRows(data.items || []);
      setEmpMap(data.employees || {});
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  }, [filters]);
  useEffect(() => { load(); }, [load]);

  const removeEvent = async (ev) => {
    const reason = window.prompt("Silme nedeni:");
    if (!reason || !reason.trim()) return;
    try {
      await api.delete(`/pdks/events/${ev.id}`, { params: { reason } });
      toast.success("Kayıt silindi (soft delete)");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="p-4 sm:p-6 space-y-6" data-testid="pdks-movements-page">
      <PageHeader title="Günlük Hareketler" subtitle="PDKS giriş / çıkış kayıtları" />

      <Card className="bg-slate-900/40 border-slate-800 rounded-sm p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div className="text-slate-100 text-sm font-semibold flex items-center gap-2">
            <Filter className="w-4 h-4" /> Filtreler
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => setExpanded((v) => !v)} className="text-slate-400 hover:text-slate-100">
              {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </Button>
            <Button size="sm" onClick={() => setManualOpen(true)} className="bg-blue-600 hover:bg-blue-500 rounded-sm" data-testid="pdks-movements-add-btn">
              <Plus className="w-3.5 h-3.5 mr-1" /> Manuel Kayıt
            </Button>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
          <FilterField label="Tarih">
            <Input type="date" value={filters.date} onChange={(e) => setFilters({ ...filters, date: e.target.value })}
              className="bg-slate-950 border-slate-800 rounded-sm h-8 text-xs font-mono" data-testid="pdks-filter-date" />
          </FilterField>
          {expanded && (
            <>
              <FilterField label="Başlangıç">
                <Input type="date" value={filters.start_date} onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
                  className="bg-slate-950 border-slate-800 rounded-sm h-8 text-xs font-mono" />
              </FilterField>
              <FilterField label="Bitiş">
                <Input type="date" value={filters.end_date} onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
                  className="bg-slate-950 border-slate-800 rounded-sm h-8 text-xs font-mono" />
              </FilterField>
            </>
          )}
          <FilterField label="Departman">
            <Select value={filters.department || "all"} onValueChange={(v) => setFilters({ ...filters, department: v === "all" ? "" : v })}>
              <SelectTrigger className="bg-slate-950 border-slate-800 rounded-sm h-8 text-xs"><SelectValue placeholder="Tümü" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tümü</SelectItem>
                {departments.map((d) => <SelectItem key={d.code || d.name} value={d.code || d.name}>{d.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </FilterField>
          <FilterField label="Hareket Türü">
            <Select value={filters.event_type || "all"} onValueChange={(v) => setFilters({ ...filters, event_type: v === "all" ? "" : v })}>
              <SelectTrigger className="bg-slate-950 border-slate-800 rounded-sm h-8 text-xs"><SelectValue placeholder="Tümü" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tümü</SelectItem>
                {Object.entries(meta.event_type_labels).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </FilterField>
          <FilterField label="Kaynak">
            <Select value={filters.source || "all"} onValueChange={(v) => setFilters({ ...filters, source: v === "all" ? "" : v })}>
              <SelectTrigger className="bg-slate-950 border-slate-800 rounded-sm h-8 text-xs"><SelectValue placeholder="Tümü" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tümü</SelectItem>
                {Object.entries(meta.source_labels).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </FilterField>
        </div>
      </Card>

      <Card className="bg-slate-900/40 border-slate-800 rounded-sm overflow-hidden" data-testid="pdks-movements-table">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-slate-950/70 text-slate-400 uppercase tracking-wider text-[10px]">
                <th className="text-left px-3 py-2">Personel</th>
                <th className="text-left px-3 py-2">Departman</th>
                <th className="text-left px-3 py-2">Tarih</th>
                <th className="text-left px-3 py-2">Saat</th>
                <th className="text-left px-3 py-2">Hareket</th>
                <th className="text-left px-3 py-2">Kaynak</th>
                <th className="text-left px-3 py-2">Açıklama</th>
                <th className="text-right px-3 py-2">İşlemler</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {loading ? (
                <tr><td colSpan={8} className="text-center py-6 text-slate-500"><Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Yükleniyor...</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={8} className="text-center py-6 text-slate-500">Kayıt yok.</td></tr>
              ) : rows.map((r) => {
                const emp = empMap[r.employee_id];
                return (
                  <tr key={r.id} className="text-slate-300 hover:bg-slate-900/40" data-testid={`pdks-event-row-${r.id}`}>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        <EmployeeAvatar emp={emp} />
                        <div className="text-slate-100">{emp?.name || r.employee_id}</div>
                      </div>
                    </td>
                    <td className="px-3 py-2 text-slate-400">{emp?.department || "—"}</td>
                    <td className="px-3 py-2 font-mono text-[11px]">{r.event_date}</td>
                    <td className="px-3 py-2 font-mono text-[11px]">{r.event_time}</td>
                    <td className="px-3 py-2">{meta.event_type_labels[r.event_type] || r.event_type}</td>
                    <td className="px-3 py-2 text-slate-500 text-[10px] uppercase tracking-wider">{meta.source_labels[r.source] || r.source}</td>
                    <td className="px-3 py-2 text-slate-400 max-w-[240px] truncate" title={r.note || ""}>{r.note || "—"}</td>
                    <td className="px-3 py-2 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button size="icon" variant="ghost" onClick={() => setEditEvent(r)} className="h-6 w-6" data-testid={`pdks-edit-${r.id}`}>
                          <Edit3 className="w-3.5 h-3.5 text-slate-400" />
                        </Button>
                        <Button size="icon" variant="ghost" onClick={() => removeEvent(r)} className="h-6 w-6" data-testid={`pdks-delete-${r.id}`}>
                          <Trash2 className="w-3.5 h-3.5 text-rose-400" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      <ManualEntryDialog open={manualOpen} onClose={() => setManualOpen(false)} defaultDate={filters.date || todayIso()} onCreated={load} />
      <EditEventDialog event={editEvent} onClose={() => setEditEvent(null)} onSaved={load} />
    </div>
  );
}

function FilterField({ label, children }) {
  return (
    <div className="space-y-1">
      <Label className="text-slate-400 text-[10px] uppercase tracking-widest font-mono">{label}</Label>
      {children}
    </div>
  );
}

// ============================================================================
// Manuel giriş dialog + edit dialog
// ============================================================================

function ManualEntryDialog({ open, onClose, defaultDate, onCreated }) {
  const employees = useEmployees();
  const meta = usePdksMeta();
  const [form, setForm] = useState({
    employee_id: "", event_type: "check_in",
    event_date: defaultDate || todayIso(), event_time: "08:30",
    note: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm((f) => ({ ...f, event_date: defaultDate || todayIso() }));
  }, [open, defaultDate]);

  const submit = async () => {
    if (!form.employee_id) { toast.error("Personel seçin"); return; }
    setSaving(true);
    try {
      await api.post("/pdks/events", { ...form, source: "manual" });
      toast.success("Kayıt oluşturuldu");
      onCreated && onCreated();
      onClose();
      setForm({ employee_id: "", event_type: "check_in", event_date: todayIso(), event_time: "08:30", note: "" });
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="pdks-manual-dialog">
        <DialogHeader>
          <DialogTitle>Manuel PDKS Kaydı</DialogTitle>
          <DialogDescription>Kart okutmayı unutan personel için manuel giriş/çıkış kaydı ekleyin.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Personel</Label>
            <Select value={form.employee_id} onValueChange={(v) => setForm({ ...form, employee_id: v })}>
              <SelectTrigger className="bg-slate-950 border-slate-800 rounded-sm" data-testid="pdks-manual-employee"><SelectValue placeholder="Seçin" /></SelectTrigger>
              <SelectContent>
                {employees.filter((e) => e.status === "aktif").map((e) => (
                  <SelectItem key={e.id} value={e.id}>{e.name}{e.department ? ` · ${e.department}` : ""}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Hareket</Label>
              <Select value={form.event_type} onValueChange={(v) => setForm({ ...form, event_type: v })}>
                <SelectTrigger className="bg-slate-950 border-slate-800 rounded-sm" data-testid="pdks-manual-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(meta.event_type_labels).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Tarih</Label>
              <Input type="date" value={form.event_date} onChange={(e) => setForm({ ...form, event_date: e.target.value })}
                className="bg-slate-950 border-slate-800 rounded-sm font-mono" data-testid="pdks-manual-date" />
            </div>
            <div>
              <Label className="text-xs">Saat</Label>
              <Input type="time" value={form.event_time} onChange={(e) => setForm({ ...form, event_time: e.target.value })}
                className="bg-slate-950 border-slate-800 rounded-sm font-mono" data-testid="pdks-manual-time" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Açıklama</Label>
            <Textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })}
              className="bg-slate-950 border-slate-800 rounded-sm" rows={2}
              placeholder="Örn: Kart okutmayı unuttu" data-testid="pdks-manual-note" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-sm">İptal</Button>
          <Button onClick={submit} disabled={saving} className="bg-emerald-600 hover:bg-emerald-500 rounded-sm" data-testid="pdks-manual-save">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Kaydet
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditEventDialog({ event, onClose, onSaved }) {
  const meta = usePdksMeta();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (event) setForm({
      event_type: event.event_type, event_date: event.event_date,
      event_time: event.event_time, note: event.note || "", reason: "",
    });
  }, [event]);

  if (!event || !form) return null;

  const submit = async () => {
    if (!form.reason.trim()) { toast.error("Değişiklik nedeni zorunlu"); return; }
    setSaving(true);
    try {
      await api.patch(`/pdks/events/${event.id}`, form);
      toast.success("Kayıt güncellendi (audit log oluşturuldu)");
      onSaved && onSaved();
      onClose();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={!!event} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="pdks-edit-dialog">
        <DialogHeader>
          <DialogTitle>PDKS Kaydını Düzenle</DialogTitle>
          <DialogDescription>Değişiklik nedeni audit log'a kaydedilecektir.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Hareket</Label>
              <Select value={form.event_type} onValueChange={(v) => setForm({ ...form, event_type: v })}>
                <SelectTrigger className="bg-slate-950 border-slate-800 rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(meta.event_type_labels).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Tarih</Label>
              <Input type="date" value={form.event_date} onChange={(e) => setForm({ ...form, event_date: e.target.value })}
                className="bg-slate-950 border-slate-800 rounded-sm font-mono" />
            </div>
            <div>
              <Label className="text-xs">Saat</Label>
              <Input type="time" value={form.event_time} onChange={(e) => setForm({ ...form, event_time: e.target.value })}
                className="bg-slate-950 border-slate-800 rounded-sm font-mono" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Açıklama</Label>
            <Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })}
              className="bg-slate-950 border-slate-800 rounded-sm" />
          </div>
          <div>
            <Label className="text-xs text-blue-400">Değişiklik Nedeni *</Label>
            <Textarea value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })}
              className="bg-slate-950 border-slate-800 rounded-sm" rows={2}
              placeholder="Neden değiştiriyorsunuz?" data-testid="pdks-edit-reason" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-sm">İptal</Button>
          <Button onClick={submit} disabled={saving} className="bg-emerald-600 hover:bg-emerald-500 rounded-sm" data-testid="pdks-edit-save">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Kaydet
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// 3) Puantaj — aylık özet
// ============================================================================

export function PdksTimesheet() {
  const [month, setMonth] = useState(monthIso());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null); // employee_id
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/pdks/timesheet", { params: { month } });
      setData(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [month]);
  useEffect(() => { load(); }, [load]);

  const openDetail = async (row) => {
    if (expanded === row.employee.id) { setExpanded(null); setDetail(null); return; }
    setExpanded(row.employee.id);
    try {
      const { data } = await api.get(`/pdks/employee/${row.employee.id}/summary`, { params: { month } });
      setDetail(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="p-4 sm:p-6 space-y-6" data-testid="pdks-timesheet-page">
      <PageHeader title="Puantaj" subtitle="Aylık personel puantajı" />
      <div className="flex items-center gap-2">
        <Label className="text-xs">Ay:</Label>
        <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)}
          className="bg-slate-950 border-slate-800 rounded-sm w-40 h-9 font-mono text-xs"
          data-testid="pdks-timesheet-month" />
        <Button variant="outline" size="sm" onClick={load} className="border-slate-700 rounded-sm h-9">
          <RefreshCcw className="w-3.5 h-3.5 mr-1" /> Yenile
        </Button>
      </div>

      <Card className="bg-slate-900/40 border-slate-800 rounded-sm overflow-hidden" data-testid="pdks-timesheet-table">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-slate-950/70 text-slate-400 uppercase tracking-wider text-[10px]">
                <th className="text-left px-3 py-2">Personel</th>
                <th className="text-left px-3 py-2">Departman</th>
                <th className="text-right px-3 py-2">Beklenen İş Günü</th>
                <th className="text-right px-3 py-2">Çalışılan Gün</th>
                <th className="text-right px-3 py-2">Toplam Çalışma</th>
                <th className="text-right px-3 py-2">Geç Kalma</th>
                <th className="text-right px-3 py-2">Erken Çıkma</th>
                <th className="text-right px-3 py-2">Devamsız</th>
                <th className="text-right px-3 py-2">İzin</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {loading ? (
                <tr><td colSpan={9} className="text-center py-6 text-slate-500"><Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Yükleniyor...</td></tr>
              ) : (data?.rows || []).length === 0 ? (
                <tr><td colSpan={9} className="text-center py-6 text-slate-500">Kayıt yok.</td></tr>
              ) : (data.rows.map((r) => (
                <Fragment key={r.employee.id}>
                  <tr className="text-slate-300 hover:bg-slate-900/40 cursor-pointer"
                    onClick={() => openDetail(r)} data-testid={`pdks-ts-row-${r.employee.id}`}>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        <EmployeeAvatar emp={r.employee} />
                        <div className="text-slate-100">{r.employee.name}</div>
                      </div>
                    </td>
                    <td className="px-3 py-2 text-slate-400">{r.employee.department || "—"}</td>
                    <td className="px-3 py-2 text-right font-mono">{r.expected_work_days}</td>
                    <td className="px-3 py-2 text-right font-mono text-emerald-400">{r.worked_days}</td>
                    <td className="px-3 py-2 text-right font-mono">{fmtMinutes(r.worked_minutes)}</td>
                    <td className={`px-3 py-2 text-right font-mono ${r.late_minutes > 0 ? "text-blue-400" : ""}`}>{r.late_minutes > 0 ? fmtMinutes(r.late_minutes) : "—"}</td>
                    <td className={`px-3 py-2 text-right font-mono ${r.early_leave_minutes > 0 ? "text-blue-400" : ""}`}>{r.early_leave_minutes > 0 ? fmtMinutes(r.early_leave_minutes) : "—"}</td>
                    <td className={`px-3 py-2 text-right font-mono ${r.absent_days > 0 ? "text-rose-400" : ""}`}>{r.absent_days || "—"}</td>
                    <td className="px-3 py-2 text-right font-mono text-amber-400">{r.on_leave_days || "—"}</td>
                  </tr>
                  {expanded === r.employee.id && detail && (
                    <tr>
                      <td colSpan={9} className="bg-slate-950/60 px-3 py-3">
                        <DayDetailGrid days={detail.days} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              )))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function DayDetailGrid({ days }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-1.5">
      {days.map((d) => {
        const s = STATUS_STYLE[d.status] || STATUS_STYLE.unknown;
        const dow = new Date(d.date + "T00:00:00").getDay();
        const dayLabel = DAYS_TR[dow === 0 ? 6 : dow - 1];
        return (
          <div key={d.date} className={`text-[10px] p-2 rounded-sm border ${s.cls}`}>
            <div className="font-mono">{d.date.slice(-2)} · {dayLabel}</div>
            {d.first_check_in && <div className="text-slate-100 font-mono">{d.first_check_in}→{d.last_check_out || "..."}</div>}
            <div className="uppercase tracking-widest">{s.label}</div>
            {d.late_minutes > 0 && <div className="text-blue-400 font-mono">Geç {d.late_minutes}dk</div>}
          </div>
        );
      })}
    </div>
  );
}

// ============================================================================
// 4) PDKS Ayarları
// ============================================================================

export function PdksSettingsPage() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/pdks/settings");
      setCfg(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...cfg };
      delete payload.updated_at;
      const { data } = await api.patch("/pdks/settings", payload);
      setCfg(data);
      toast.success("Ayarlar kaydedildi");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const runAbsenceNow = async () => {
    try {
      const { data } = await api.post("/pdks/absence-alerts/run-now");
      if (data.absent_count === 0) {
        toast.success("Devamsız personel yok — bildirim yok");
      } else {
        toast.success(`${data.absent_count} devamsız · ${data.sent}/${data.recipients} HR alıcısına gönderildi`);
      }
      if ((data.errors || []).length) {
        toast.error(data.errors[0]);
      }
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (loading || !cfg) {
    return <div className="p-6"><Loader2 className="w-4 h-4 animate-spin" /> Yükleniyor...</div>;
  }

  const toggleDay = (d) => {
    const cur = cfg.work_days || [];
    const next = cur.includes(d) ? cur.filter((x) => x !== d) : [...cur, d].sort();
    setCfg({ ...cfg, work_days: next });
  };

  return (
    <div className="p-4 sm:p-6 space-y-6" data-testid="pdks-settings-page">
      <PageHeader title="PDKS Ayarları" subtitle="Çalışma saatleri, mola ve tolerans ayarları" />

      <Card className="bg-slate-900/40 border-slate-800 rounded-sm p-5 space-y-5 max-w-3xl">
        <div>
          <Label className="text-xs uppercase tracking-widest font-mono text-slate-400">Çalışma Günleri</Label>
          <div className="flex gap-1.5 mt-2 flex-wrap">
            {DAYS_TR.map((label, idx) => {
              const d = idx + 1;
              const active = (cfg.work_days || []).includes(d);
              return (
                <button key={d} type="button" onClick={() => toggleDay(d)} data-testid={`pdks-day-${d}`}
                  className={`px-3 py-1.5 rounded-sm text-xs uppercase tracking-widest font-mono border ${active ? "bg-emerald-950 border-emerald-800 text-emerald-300" : "bg-slate-950 border-slate-800 text-slate-500"}`}>
                  {label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <TimeField label="İşe Giriş Saati" value={cfg.work_start_time} onChange={(v) => setCfg({ ...cfg, work_start_time: v })} testId="pdks-work-start" />
          <TimeField label="İşten Çıkış Saati" value={cfg.work_end_time} onChange={(v) => setCfg({ ...cfg, work_end_time: v })} testId="pdks-work-end" />
          <TimeField label="Öğle Molası Başlangıcı" value={cfg.lunch_start_time} onChange={(v) => setCfg({ ...cfg, lunch_start_time: v })} testId="pdks-lunch-start" />
          <TimeField label="Öğle Molası Bitişi" value={cfg.lunch_end_time} onChange={(v) => setCfg({ ...cfg, lunch_end_time: v })} testId="pdks-lunch-end" />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <NumField label="Geç Kalma Toleransı (dk)" value={cfg.late_tolerance_min} onChange={(v) => setCfg({ ...cfg, late_tolerance_min: v })} testId="pdks-tol-late" />
          <NumField label="Erken Çıkış Toleransı (dk)" value={cfg.early_leave_tolerance_min} onChange={(v) => setCfg({ ...cfg, early_leave_tolerance_min: v })} testId="pdks-tol-early" />
          <NumField label="Fazla Mesai Toleransı (dk)" value={cfg.overtime_start_tolerance_min} onChange={(v) => setCfg({ ...cfg, overtime_start_tolerance_min: v })} testId="pdks-tol-ot" />
          <NumField label="Günlük Standart Çalışma (dk)" value={cfg.daily_standard_minutes} onChange={(v) => setCfg({ ...cfg, daily_standard_minutes: v })} testId="pdks-std-daily" />
          <NumField label="Art Arda Kayıt Engeli (sn)" value={cfg.min_seconds_between_events} onChange={(v) => setCfg({ ...cfg, min_seconds_between_events: v })} testId="pdks-min-sec-between" />
          <NumField label="Terminal Otomatik Kilit (sn)" value={cfg.auto_lock_terminal_after_sec} onChange={(v) => setCfg({ ...cfg, auto_lock_terminal_after_sec: v })} testId="pdks-auto-lock" />
          <div className="flex items-center gap-2 pt-6">
            <input type="checkbox" id="pin_enabled" checked={!!cfg.pin_enabled}
              onChange={(e) => setCfg({ ...cfg, pin_enabled: e.target.checked })}
              data-testid="pdks-pin-enabled" />
            <Label htmlFor="pin_enabled" className="text-xs">PIN girişi terminalde aktif</Label>
          </div>
        </div>

        {/* Absence notification */}
        <div className="pt-4 border-t border-slate-800 space-y-3">
          <div className="text-slate-100 font-semibold text-sm flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-400" /> Otomatik Devamsızlık Bildirimi (WhatsApp)
          </div>
          <div className="text-slate-400 text-[11px]">Her çalışma günü belirtilen saatte, giriş yapmamış ve izinli olmayan personellerin listesi HR'ye WhatsApp ile gönderilir.</div>
          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-center gap-2 pt-6">
              <input type="checkbox" id="absence_notify_enabled" checked={!!cfg.absence_notify_enabled}
                onChange={(e) => setCfg({ ...cfg, absence_notify_enabled: e.target.checked })}
                data-testid="pdks-absence-enabled" />
              <Label htmlFor="absence_notify_enabled" className="text-xs">Devamsızlık bildirimi aktif</Label>
            </div>
            <TimeField label="Bildirim Saati" value={cfg.absence_notify_time || "09:15"}
              onChange={(v) => setCfg({ ...cfg, absence_notify_time: v })} testId="pdks-absence-time" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest font-mono text-slate-400">HR Alıcı Telefonları (virgülle ayır, E.164)</Label>
            <Input
              value={(cfg.absence_notify_phones || []).join(", ")}
              onChange={(e) => setCfg({ ...cfg, absence_notify_phones: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
              placeholder="+90532XXXXXXX, +90533XXXXXXX"
              className="bg-slate-950 border-slate-800 rounded-sm font-mono text-xs"
              data-testid="pdks-absence-phones" />
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={runAbsenceNow} disabled={!cfg.absence_notify_enabled}
              className="border-slate-700 rounded-sm" data-testid="pdks-absence-run-now">
              Şimdi Tara ve Gönder (test)
            </Button>
          </div>
        </div>

        <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-500 rounded-sm" data-testid="pdks-settings-save">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Kaydet
        </Button>
      </Card>

      <PdksTerminalsCard />
      <PdksApkCard />
    </div>
  );
}

function TimeField({ label, value, onChange, testId }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-widest font-mono text-slate-400">{label}</Label>
      <Input type="time" value={value || ""} onChange={(e) => onChange(e.target.value)}
        className="bg-slate-950 border-slate-800 rounded-sm font-mono" data-testid={testId} />
    </div>
  );
}
function NumField({ label, value, onChange, testId }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-widest font-mono text-slate-400">{label}</Label>
      <Input type="number" min="0" value={value ?? 0} onChange={(e) => onChange(parseInt(e.target.value || "0", 10))}
        className="bg-slate-950 border-slate-800 rounded-sm font-mono" data-testid={testId} />
    </div>
  );
}

// ============================================================================
// 4b) Terminals — PDKS Ayarları içinden yönetilir
// ============================================================================

export function PdksTerminalsCard() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [code, setCode] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [newToken, setNewToken] = useState(null); // {terminal_code, token}

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/pdks/terminals");
      setRows(data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!code.trim()) { toast.error("Terminal kodu zorunlu"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/pdks/terminals", { code: code.trim(), note });
      setNewToken({ code: data.terminal.code, token: data.token });
      setCode(""); setNote(""); load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const rotate = async (id) => {
    if (!window.confirm("Yeni token üretilsin mi? Eski token geçersiz olacak.")) return;
    try {
      const { data } = await api.post(`/pdks/terminals/${id}/rotate`);
      setNewToken({ code: rows.find((r) => r.id === id)?.code || "", token: data.token });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const setPin = async (t) => {
    const pin = window.prompt(`${t.code} için 4–6 haneli kiosk yönetici PIN'i:`);
    if (pin === null) return;
    if (!/^\d{4,6}$/.test(pin)) { toast.error("PIN 4–6 haneli rakam olmalı"); return; }
    try {
      await api.put(`/pdks/terminals/${t.id}/admin-pin`, { pin });
      toast.success("Yönetici PIN'i kaydedildi");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggle = async (t) => {
    try {
      await api.patch(`/pdks/terminals/${t.id}`, { is_active: !t.is_active });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const copyToken = () => {
    if (!newToken) return;
    navigator.clipboard?.writeText(newToken.token);
    toast.success("Token kopyalandı");
  };

  return (
    <Card className="bg-slate-900/40 border-slate-800 rounded-sm p-5 space-y-4 max-w-3xl" data-testid="pdks-terminals-card">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-slate-100 font-semibold text-sm flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-emerald-400" /> PDKS Terminalleri</div>
          <div className="text-slate-400 text-xs mt-1">Her tablet için bir terminal kodu ve token oluşturun. Token bir kez gösterilir.</div>
        </div>
        <Button size="sm" variant="outline" onClick={load} className="rounded-sm"><RefreshCcw className="w-3.5 h-3.5" /></Button>
      </div>

      {newToken && (
        <div className="bg-emerald-950/30 border border-emerald-800 rounded-sm p-3 space-y-2" data-testid="pdks-new-token">
          <div className="text-emerald-300 text-xs uppercase tracking-widest font-mono">Yeni Terminal Token — Sadece Bir Kez Gösterilir</div>
          <div className="text-slate-100 text-xs">Kod: <span className="font-mono">{newToken.code}</span></div>
          <div className="flex items-center gap-2">
            <code className="flex-1 bg-slate-950 border border-slate-800 rounded-sm px-3 py-2 text-emerald-300 font-mono text-xs break-all">{newToken.token}</code>
            <Button size="sm" onClick={copyToken} className="bg-emerald-600 hover:bg-emerald-500 rounded-sm" data-testid="pdks-token-copy"><Copy className="w-3.5 h-3.5 mr-1" /> Kopyala</Button>
            <Button size="sm" variant="ghost" onClick={() => setNewToken(null)}><Check className="w-3.5 h-3.5" /></Button>
          </div>
          <div className="text-slate-400 text-[11px]">Bu token'ı tabletteki <code className="text-slate-300">/pdks/terminal</code> ekranına yapıştırın.</div>
        </div>
      )}

      <div className="grid grid-cols-3 gap-2">
        <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="FUCAN-MAIN-ENTRANCE-01"
          className="bg-slate-950 border-slate-800 rounded-sm text-xs font-mono" data-testid="pdks-terminal-code" />
        <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Not (ör: ana kapı)"
          className="bg-slate-950 border-slate-800 rounded-sm text-xs" data-testid="pdks-terminal-note" />
        <Button onClick={create} disabled={busy} className="bg-blue-600 hover:bg-blue-500 rounded-sm" data-testid="pdks-terminal-create">
          {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
          Terminal Oluştur
        </Button>
      </div>

      <div className="overflow-x-auto border border-slate-800 rounded-sm">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-slate-950/70 text-slate-400 uppercase tracking-wider text-[10px]">
              <th className="text-left px-3 py-2">Kod</th>
              <th className="text-left px-3 py-2">Not</th>
              <th className="text-left px-3 py-2">Son Bağlantı</th>
              <th className="text-left px-3 py-2">IP</th>
              <th className="text-left px-3 py-2">Durum</th>
              <th className="text-right px-3 py-2">İşlemler</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800">
            {loading ? (
              <tr><td colSpan={6} className="text-center py-6 text-slate-500"><Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Yükleniyor...</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={6} className="text-center py-6 text-slate-500">Terminal yok.</td></tr>
            ) : rows.map((t) => (
              <tr key={t.id} className="text-slate-300" data-testid={`pdks-terminal-row-${t.id}`}>
                <td className="px-3 py-2 font-mono">{t.code}</td>
                <td className="px-3 py-2 text-slate-400">{t.note || "—"}</td>
                <td className="px-3 py-2 font-mono text-[11px]">{t.last_seen_at ? new Date(t.last_seen_at).toLocaleString("tr-TR") : "—"}</td>
                <td className="px-3 py-2 font-mono text-[11px] text-slate-500">{t.last_ip || "—"}</td>
                <td className="px-3 py-2">
                  <Badge className={`rounded-sm text-[10px] uppercase tracking-wider ${t.is_active ? "bg-emerald-950 text-emerald-300 border-emerald-800" : "bg-slate-900 text-slate-400 border-slate-700"}`}>
                    {t.is_active ? "Aktif" : "Pasif"}
                  </Badge>
                </td>
                <td className="px-3 py-2 text-right">
                  <Button size="sm" variant="ghost" onClick={() => setPin(t)} className={t.has_admin_pin ? "text-emerald-400" : "text-amber-400"} data-testid={`pdks-terminal-pin-${t.id}`}>
                    {t.has_admin_pin ? "PIN Değiştir" : "Yönetici PIN Tanımla"}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => rotate(t.id)} className="text-blue-400" data-testid={`pdks-terminal-rotate-${t.id}`}>
                    <RefreshCcw className="w-3.5 h-3.5 mr-1" /> Token Yenile
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => toggle(t)} className="text-slate-400 ml-1" data-testid={`pdks-terminal-toggle-${t.id}`}>
                    {t.is_active ? "Pasif" : "Aktif"}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

// ============================================================================
// 4c) Fazla Mesai
// ============================================================================

export function PdksOvertime() {
  const [month, setMonth] = useState(monthIso());
  const [items, setItems] = useState([]);
  const [empMap, setEmpMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/pdks/overtime", { params: { month } });
      setItems(data.items || []);
      setEmpMap(data.employees || {});
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [month]);
  useEffect(() => { load(); }, [load]);

  const recalc = async () => {
    setBusy(true);
    try {
      await api.post(`/pdks/overtime/recalculate`, null, { params: { month } });
      toast.success("Fazla mesai yeniden hesaplandı");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const decide = async (id, decision) => {
    const note = window.prompt(`${decision === "approve" ? "Onay" : "Red"} notu (opsiyonel):`) || "";
    try {
      await api.post(`/pdks/overtime/${id}/${decision}`, { note });
      toast.success(decision === "approve" ? "Onaylandı" : "Reddedildi");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const badgeCls = (s) => s === "onaylandi" ? "bg-emerald-950 text-emerald-300 border-emerald-800"
                    : s === "reddedildi" ? "bg-rose-950 text-rose-300 border-rose-800"
                    : "bg-amber-950 text-amber-300 border-amber-800";
  const badgeLbl = (s) => s === "onaylandi" ? "Onaylandı" : s === "reddedildi" ? "Reddedildi" : "Bekliyor";

  return (
    <div className="p-4 sm:p-6 space-y-6" data-testid="pdks-overtime-page">
      <PageHeader title="Fazla Mesai" subtitle="Otomatik hesaplanan fazla mesailer — onay bekliyor" />
      <div className="flex items-center gap-2">
        <Label className="text-xs">Ay:</Label>
        <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)}
          className="bg-slate-950 border-slate-800 rounded-sm w-40 h-9 font-mono text-xs"
          data-testid="pdks-overtime-month" />
        <Button variant="outline" size="sm" onClick={recalc} disabled={busy} className="border-slate-700 rounded-sm h-9" data-testid="pdks-overtime-recalc">
          {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <RefreshCcw className="w-3.5 h-3.5 mr-1" />}
          Yeniden Hesapla
        </Button>
      </div>

      <Card className="bg-slate-900/40 border-slate-800 rounded-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-slate-950/70 text-slate-400 uppercase tracking-wider text-[10px]">
                <th className="text-left px-3 py-2">Personel</th>
                <th className="text-left px-3 py-2">Departman</th>
                <th className="text-left px-3 py-2">Tarih</th>
                <th className="text-right px-3 py-2">Süre</th>
                <th className="text-left px-3 py-2">Durum</th>
                <th className="text-left px-3 py-2">Onay Notu</th>
                <th className="text-right px-3 py-2">İşlem</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {loading ? (
                <tr><td colSpan={7} className="text-center py-6 text-slate-500"><Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Yükleniyor...</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={7} className="text-center py-6 text-slate-500">Bu ay fazla mesai yok. "Yeniden Hesapla" ile üretebilirsiniz.</td></tr>
              ) : items.map((r) => {
                const emp = empMap[r.employee_id];
                return (
                  <tr key={r.id} className="text-slate-300 hover:bg-slate-900/40" data-testid={`pdks-ot-row-${r.id}`}>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        <EmployeeAvatar emp={emp} />
                        <div className="text-slate-100">{emp?.name || r.employee_id}</div>
                      </div>
                    </td>
                    <td className="px-3 py-2 text-slate-400">{emp?.department || "—"}</td>
                    <td className="px-3 py-2 font-mono text-[11px]">{r.date}</td>
                    <td className="px-3 py-2 text-right font-mono text-blue-400">{fmtMinutes(r.minutes)}</td>
                    <td className="px-3 py-2">
                      <Badge className={`${badgeCls(r.status)} rounded-sm text-[10px] uppercase tracking-wider`}>{badgeLbl(r.status)}</Badge>
                    </td>
                    <td className="px-3 py-2 text-slate-500 text-[11px]">{r.note || "—"}</td>
                    <td className="px-3 py-2 text-right">
                      {r.status === "bekliyor" && (
                        <>
                          <Button size="sm" variant="ghost" onClick={() => decide(r.id, "approve")} className="text-emerald-400" data-testid={`pdks-ot-approve-${r.id}`}>Onayla</Button>
                          <Button size="sm" variant="ghost" onClick={() => decide(r.id, "reject")} className="text-rose-400" data-testid={`pdks-ot-reject-${r.id}`}>Reddet</Button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

// ============================================================================
// 4d) Toplu Yüz Kaydı — mevcut profil fotoğraflarından descriptor üret
// ============================================================================

export function BulkFaceCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0, current: "", ok: 0, fail: 0, errors: [] });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/pdks/face/bulk-candidates");
      setData(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const run = async () => {
    if (!data?.candidates?.length) return;
    setRunning(true);
    // faceRecognition helper'larını lazy import — sayfa açılırken hazır olur
    const fr = await import("@/lib/faceRecognition");
    await fr.ensureFaceModels();

    const total = data.candidates.length;
    let done = 0, ok = 0, fail = 0;
    const errors = [];
    setProgress({ done, total, current: "", ok, fail, errors });

    for (const emp of data.candidates) {
      setProgress((p) => ({ ...p, current: emp.name || emp.id }));
      try {
        const img = document.createElement("img");
        img.crossOrigin = "anonymous";
        img.src = emp.photo_url + (emp.photo_url.includes("?") ? "&" : "?") + "t=bulk";
        await new Promise((res, rej) => {
          img.onload = res;
          img.onerror = () => rej(new Error("Foto indirilemedi"));
        });
        const res = await fr.detectSingleFaceWithDescriptor(img);
        if (!res || res.multiple) {
          fail++; errors.push(`${emp.name}: yüz algılanmadı veya birden fazla yüz`);
        } else {
          // Tek örnekli enrollment: tek descriptor. face-api yeterince
          // sağlam olduğu için tek örnek bulk için makul.
          await api.post(`/pdks/face/enroll/${emp.id}`, { descriptors: [res.descriptor] });
          ok++;
        }
      } catch (e) {
        fail++;
        errors.push(`${emp.name}: ${e?.message || "hata"}`);
      }
      done++;
      setProgress({ done, total, current: emp.name || emp.id, ok, fail, errors: errors.slice(-5) });
    }
    setRunning(false);
    toast.success(`Toplu yüz kaydı bitti · ${ok} başarılı, ${fail} hatalı`);
    load();
  };

  if (loading || !data) return null;
  const c = data.candidates || [];

  return (
    <Card className="bg-slate-900/40 border-slate-800 rounded-sm p-5 space-y-3 max-w-3xl" data-testid="pdks-bulk-face-card">
      <div className="flex items-center justify-between gap-2">
        <div>
          <div className="text-slate-100 font-semibold text-sm flex items-center gap-2">
            <Camera className="w-4 h-4 text-blue-400" /> Toplu Yüz Kaydı
          </div>
          <div className="text-slate-400 text-[11px] mt-1">
            Fotoğraflı ama yüz kaydı olmayan {c.length} personel · Zaten kayıtlı: {data.already_enrolled} · Fotoğrafsız: {data.no_photo}
          </div>
        </div>
        <Button onClick={run} disabled={running || c.length === 0}
          className="bg-blue-600 hover:bg-blue-500 rounded-sm" data-testid="pdks-bulk-run">
          {running ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Camera className="w-4 h-4 mr-2" />}
          {running ? `${progress.done}/${progress.total}` : "Toplu Kaydı Başlat"}
        </Button>
      </div>
      {running && (
        <div className="text-xs text-slate-300 space-y-1">
          <div>İşlenen: <span className="text-slate-100">{progress.current}</span></div>
          <div className="flex gap-3 text-[11px]">
            <span className="text-emerald-400">Başarılı: {progress.ok}</span>
            <span className="text-rose-400">Hatalı: {progress.fail}</span>
          </div>
          <div className="h-2 bg-slate-800 rounded-sm overflow-hidden">
            <div className="h-full bg-blue-500 transition-all" style={{ width: progress.total ? `${(progress.done / progress.total) * 100}%` : 0 }} />
          </div>
        </div>
      )}
      {progress.errors.length > 0 && (
        <div className="text-[11px] text-rose-400 space-y-0.5" data-testid="pdks-bulk-errors">
          {progress.errors.map((e, i) => <div key={i}>• {e}</div>)}
        </div>
      )}
    </Card>
  );
}


export function PdksEmployeeTab({ employeeId }) {
  const [month, setMonth] = useState(monthIso());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [faceStatus, setFaceStatus] = useState(null);
  const [pinStatus, setPinStatus] = useState(null);
  const [faceOpen, setFaceOpen] = useState(false);
  const [pinOpen, setPinOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sum, face, pin] = await Promise.all([
        api.get(`/pdks/employee/${employeeId}/summary`, { params: { month } }),
        api.get(`/pdks/face/status/${employeeId}`).catch(() => ({ data: { registered: false } })),
        api.get(`/pdks/pin/status/${employeeId}`).catch(() => ({ data: { registered: false } })),
      ]);
      setData(sum.data);
      setFaceStatus(face.data);
      setPinStatus(pin.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [employeeId, month]);
  useEffect(() => { load(); }, [load]);

  const disableFace = async () => {
    if (!window.confirm("Yüz kaydı pasifleştirilsin mi?")) return;
    try { await api.delete(`/pdks/face/${employeeId}`); toast.success("Yüz kaydı pasifleştirildi"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const clearPin = async () => {
    if (!window.confirm("PIN silinsin mi?")) return;
    try { await api.delete(`/pdks/pin/${employeeId}`); toast.success("PIN silindi"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  if (loading || !data) return <div className="text-slate-400 text-sm"><Loader2 className="w-4 h-4 animate-spin inline mr-1" /> PDKS yükleniyor...</div>;

  const t = data.today;
  const tot = data.month_totals;
  const empName = data.employee?.name || "";

  return (
    <div className="space-y-5" data-testid="pdks-employee-tab">
      <div className="flex items-center gap-2 flex-wrap">
        <Label className="text-xs">Ay:</Label>
        <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)}
          className="bg-slate-950 border-slate-800 rounded-sm w-40 h-9 font-mono text-xs" />
        <div className="flex-1" />
        {/* PIN kartı (yüz tanıma kaldırıldı — giriş/çıkış QR ile) */}
        <div className="flex flex-wrap gap-2">
          <BiometricPill
            icon={<KeyRound className="w-3.5 h-3.5" />} label="PIN"
            active={!!pinStatus?.registered}
            actionLabel={pinStatus?.registered ? "Değiştir" : "Ata"}
            onAction={() => setPinOpen(true)}
            onClear={pinStatus?.registered ? clearPin : null}
            testId="emp-pin"
          />
          <MobileAccessPill employeeId={employeeId} employeeName={empName} />
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
        <MiniStat label="Bugün Durum" value={<StatusBadge status={t.status} isLate={t.is_late} isEarly={t.is_early} />} />
        <MiniStat label="Bugün Giriş / Çıkış" value={<span className="font-mono">{t.first_check_in || "—"} · {t.last_check_out || "—"}</span>} />
        <MiniStat label="Bu Ay Çalışma" value={fmtMinutes(tot.worked_minutes)} accent="emerald" />
        <MiniStat label="Bu Ay Geç Kalma" value={fmtMinutes(tot.late_minutes)} accent="orange" />
        <MiniStat label="Bu Ay Erken Çıkma" value={fmtMinutes(tot.early_leave_minutes)} accent="orange" />
        <MiniStat label="Devamsız Gün" value={tot.absent_days} accent="rose" />
        <MiniStat label="Çalışılan Gün" value={tot.worked_days} accent="emerald" />
      </div>

      <Card className="bg-slate-900/40 border-slate-800 rounded-sm p-4">
        <div className="text-xs uppercase tracking-widest font-mono text-slate-400 mb-3">Ay Detayı</div>
        <DayDetailGrid days={data.days} />
      </Card>

      <Card className="bg-slate-900/40 border-slate-800 rounded-sm p-4">
        <div className="text-xs uppercase tracking-widest font-mono text-slate-400 mb-3">Son 20 Hareket</div>
        <div className="space-y-1">
          {(data.recent_events || []).length === 0 ? (
            <div className="text-slate-500 text-xs">Hareket yok.</div>
          ) : data.recent_events.map((ev) => (
            <div key={ev.id} className="flex items-center gap-2 text-xs text-slate-300">
              <span className="font-mono text-[11px] text-slate-500">{ev.event_date} {ev.event_time}</span>
              <span>·</span>
              <span>{ev.event_type === "check_in" ? "Giriş" : ev.event_type === "check_out" ? "Çıkış" : ev.event_type}</span>
              <span className="text-slate-500 text-[10px] uppercase tracking-wider">{ev.source}</span>
              {ev.note && <span className="text-slate-500 italic">— {ev.note}</span>}
            </div>
          ))}
        </div>
      </Card>

      <FaceEnrollDialog open={faceOpen} onClose={() => setFaceOpen(false)}
        employeeId={employeeId} employeeName={empName} onDone={load} />
      <PinSetDialog open={pinOpen} onClose={() => setPinOpen(false)}
        employeeId={employeeId} employeeName={empName} onDone={load} />
    </div>
  );
}

// PDKS Mobil Erişim — ERP kullanıcısı açmadan telefonu personel kartına bağlar
function MobileAccessPill({ employeeId, employeeName }) {
  const [st, setSt] = useState(null);
  const [actEmp, setActEmp] = useState(null);
  const load = useCallback(() => {
    api.get(`/pdks/mobile/status/${employeeId}`).then(({ data }) => setSt(data)).catch(() => setSt(false));
  }, [employeeId]);
  useEffect(() => { load(); }, [load]);
  const closeDialog = useCallback(() => { setActEmp(null); load(); }, [load]);
  if (st === false || st === null) return null;
  const revoke = async () => {
    if (!window.confirm("PDKS mobil erişimi iptal edilsin mi? Telefon yeniden aktivasyon gerektirir.")) return;
    try { await api.delete(`/pdks/mobile/${employeeId}`); toast.success("Mobil erişim iptal edildi"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="flex flex-col gap-1">
      <BiometricPill icon={<Smartphone className="w-3.5 h-3.5" />} label="PDKS Mobil" active={st.active}
        actionLabel="PDKS Aktive Et" onAction={() => setActEmp({ id: employeeId, name: employeeName })}
        onClear={st.active || st.pending_activation_expires_at ? revoke : null} testId="emp-mobile" />
      {!st.active && st.pending_activation_expires_at && (
        <div className="text-[10px] text-amber-400" data-testid="emp-mobile-pending">
          Bekleyen aktivasyon · son: {new Date(st.pending_activation_expires_at).toLocaleString("tr-TR")}
        </div>
      )}
      <PdksActivationDialog employee={actEmp} onClose={closeDialog} />
    </div>
  );
}

function BiometricPill({ icon, label, active, actionLabel, onAction, onClear, testId }) {
  return (
    <div className={`flex items-center gap-2 px-2.5 py-1.5 rounded-sm border text-xs ${active ? "border-emerald-800 bg-emerald-950/30 text-emerald-300" : "border-slate-800 bg-slate-950 text-slate-400"}`} data-testid={testId}>
      {icon}
      <span>{label}</span>
      <span className={`text-[10px] uppercase tracking-widest font-mono ${active ? "text-emerald-400" : "text-slate-500"}`}>{active ? "Aktif" : "Yok"}</span>
      <Button size="sm" variant="ghost" onClick={onAction} className="h-6 text-xs text-blue-400 hover:text-blue-300" data-testid={`${testId}-action`}>
        {actionLabel}
      </Button>
      {onClear && (
        <Button size="sm" variant="ghost" onClick={onClear} className="h-6 text-xs text-slate-500 hover:text-rose-400" data-testid={`${testId}-clear`}>
          <Ban className="w-3 h-3" />
        </Button>
      )}
    </div>
  );
}

function MiniStat({ label, value, accent = "slate" }) {
  const colors = {
    slate: "text-slate-200",
    emerald: "text-emerald-400",
    orange: "text-blue-400",
    rose: "text-rose-400",
  };
  return (
    <Card className="bg-slate-900/40 border-slate-800 rounded-sm p-3">
      <div className="text-[10px] uppercase tracking-widest font-mono text-slate-400">{label}</div>
      <div className={`text-base mt-1 ${colors[accent]}`}>{value}</div>
    </Card>
  );
}
